import { breadcrumbSchema } from "@/lib/schema";
import { STATS_TEXT } from "@/lib/stats";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { SiteLayout } from "@/components/site/Layout";
import { Picture } from "@/components/site/Picture";
import { Section, SectionEyebrow } from "@/components/site/Section";
import { CTABanner } from "@/components/site/CTABanner";
import { PILLARS } from "@/lib/pillars";
import marketTexas from "@/assets/market-texas.jpg";
import garyKeller from "@/assets/gary-keller.jpg.asset.json";
import garyKristan from "@/assets/gary-kristan.jpg.asset.json";

export const Route = createFileRoute("/why-join-kcn")({
  head: () => ({
    meta: [
      { title: "Real Estate Team vs Brokerage - Why Agents Join KCN" },
      {
        name: "description",
        content:
          "Real estate team vs brokerage: compare splits, leverage, and costs, plus the real estate team culture behind real estate teams hiring top agents.",
      },

      { property: "og:title", content: "Why Join the Kristan Cole Network" },
      {
        property: "og:description",
        content:
          "Real leverage, real mentorship, real legacy - the case for joining Kristan Cole's national team.",
      },
      { property: "og:url", content: "https://joinkcn.com/why-join-kcn" },
    ],
    links: [{ rel: "canonical", href: "https://joinkcn.com/why-join-kcn" }],
    scripts: [
      breadcrumbSchema([{ name: "Why Join KCN", path: "/why-join-kcn" }]),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "What is the KCN wealth model for agents?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "KCN coaches agents to close 24+ homes a year for roughly $250K in net income, live on the first $125K, and invest the second $125K, repeating that pattern until they're financially free. The goal isn't selling more homes - it's funding today's lifestyle and tomorrow's freedom.",
              },
            },
            {
              "@type": "Question",
              name: "Who is the right fit for the Kristan Cole Network?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Growing and top-producing agents who want to build a real, scalable business - not just chase the next deal. Agents who value mentorship, systems, faith, and family alongside production.",
              },
            },

            {
              "@type": "Question",
              name: "What does KCN provide that other teams don't?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Four decades of proven leadership, refined systems, built-in leverage, collaborative support, and direct coaching - all designed to help you spend more time serving clients and less time managing the business.",
              },
            },
            {
              "@type": "Question",
              name: "Do I have to live in Arizona or Alaska?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Our primary markets include Arizona - Greater Phoenix, Northern Arizona, Southern Arizona, and the White Mountains - as well as Alaska, including Wasilla and Anchorage. We also have affiliated locations across the country. If you're interested in opportunities in your area, we'd love to connect and explore what's available.",
              },
            },

            {
              "@type": "Question",
              name: "How do I start the conversation?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Schedule a one-on-one exploratory strategy call. We'll learn about your business, answer your questions, and determine if KCN is the right fit for your goals.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: WhyJoinPage,
});

const comparison = [
  ["Lead generation", "Generic scripts, do-it-yourself", "Documented playbook + ongoing coaching"],
  ["Operations", "Held in your head", "SOPs + transferable systems"],
  ["Team building", "Trial and error", "Org design + scorecards + cadence"],
  ["Leadership", "Hustle harder", "CEO-level mentorship + faith alignment"],
  ["Income ceiling", "Trades hours for dollars", "Compounds through leverage + expansion"],
  ["Culture", "Brokerage politics", "Calm, intentional, values-driven"],
];

function WhyJoinPage() {
  return (
    <SiteLayout>
      <section className="relative isolate min-h-[85vh] flex items-end overflow-hidden">
        <Picture
          src={marketTexas}
          alt="Skyline of a major American real estate market"
          pictureClassName="absolute inset-0 block h-full w-full"
          className="h-full w-full object-cover"
          width={1920}
          height={1280}
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/15" />
        <div className="relative container-edge pb-14 md:pb-16 pt-36 w-full">
          <p className="eyebrow text-foreground/90 mb-8">Why agents make the move</p>
          <h1 className="headline-xl max-w-5xl">
            You don't need another brokerage.
            <br />
            <span className="serif-italic text-foreground/85">You need a method.</span>
          </h1>
          <p className="mt-8 lede max-w-2xl text-foreground/90">
            KCN isn't a logo on your card. It's an operating system, a mentor,
            and a community of agents building wealth without burning out.
          </p>
          <p className="mt-4 text-sm uppercase tracking-[0.22em] text-white/90">
            Powered by the Kristan Cole Network - a KW network across {STATS_TEXT.markets} markets.
          </p>
        </div>
      </section>

      {/* 92% stat + Does this describe you? */}
      <Section tone="darker">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <p className="font-display text-[7rem] md:text-[10rem] leading-none text-primary tracking-tight">
              92<span className="text-foreground/85">%</span>
            </p>
            <p className="mt-4 text-foreground/80 text-lg leading-relaxed max-w-md">
              of agents in the nation did under{" "}
              <strong className="text-foreground">4 transactions</strong> last
              year. That's not a market problem. It's a method problem.
            </p>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <SectionEyebrow>Does this describe you?</SectionEyebrow>
            <h2 className="headline-md">
              You have grit. You're coachable. You want to{" "}
              <span className="serif-italic text-primary">grow</span> - and when
              you win, you want to{" "}
              <span className="serif-italic text-primary">give back</span>.
            </h2>
            <p className="mt-6 text-foreground/90 text-lg leading-relaxed">
              If that's you, we should talk. The Monthly Market Round Table is
              the fastest way to see what life on this team actually looks like.
            </p>
          </div>
        </div>
      </Section>

      {/* KCN WEALTH MODEL - compact */}
      <Section tone="dark">
        <div className="max-w-4xl">
          <SectionEyebrow>The KCN wealth model</SectionEyebrow>
          <h2 className="headline-md">
            Make it. Then{" "}
            <span className="serif-italic text-primary">multiply it.</span>
          </h2>
          <div className="mt-10 flex flex-wrap items-baseline gap-x-8 gap-y-4">
            {[
              ["24+", "homes / yr"],
              ["$250K", "net income"],
              ["$125K", "to live on"],
              ["$125K", "to invest"],
            ].map(([n, l]) => (
              <div key={l} className="flex items-baseline gap-2">
                <span className="font-display text-4xl md:text-5xl text-primary tracking-tight">{n}</span>
                <span className="text-sm uppercase tracking-[0.18em] text-foreground/80">{l}</span>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm uppercase tracking-[0.22em] text-primary">
            Until you're financially free.
          </p>
          <p className="mt-8 text-foreground/90 text-lg leading-relaxed max-w-2xl">
            It's not about selling more homes. It's about funding today's
            lifestyle and tomorrow's freedom, so your family, health, and
            relationships get the best of you, not what's left.
          </p>
        </div>
      </Section>



      {/* GARY KELLER ENDORSEMENT */}
      <Section tone="dark">
        <SectionEyebrow>Gary Keller's endorsement</SectionEyebrow>
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center mt-8">
          <div className="lg:col-span-3">
            <img
              src={garyKeller.url}
              alt="Gary Keller, Executive Chairman of the Board, Keller Williams Realty"
              loading="lazy"
              className="w-full aspect-square object-cover grayscale hover:grayscale-0 transition-all duration-500"
            />
          </div>
          <div className="lg:col-span-5">
            <blockquote className="font-display text-3xl md:text-[2.6rem] leading-[1.15] text-foreground border-l-2 border-primary pl-6">
              &ldquo;Kristan is one of the most qualified individuals in our
              entire industry to{" "}
              <span className="serif-italic text-primary">teach agent expansion</span>.&rdquo;
            </blockquote>
            <p className="mt-6 pl-6 text-sm uppercase tracking-[0.22em] text-foreground/70">
              Gary Keller
            </p>
            <p className="mt-2 pl-6 text-foreground/60 text-sm">
              Executive Chairman of the Board, Keller Williams Realty, Inc.
            </p>
          </div>
          <div className="lg:col-span-4">
            <img
              src={garyKristan.url}
              alt="Gary Keller and Kristan Cole together"
              loading="lazy"
              className="w-full object-cover"
            />
          </div>
        </div>
      </Section>

      <Section tone="cream">
        <SectionEyebrow>The honest comparison</SectionEyebrow>
        <h2 className="headline-lg max-w-3xl mb-12">
          What changes when you leave the grind behind.
        </h2>

        <div className="border border-cream-foreground/20">
          <div className="grid grid-cols-3 bg-cream-foreground text-cream text-xs uppercase tracking-[0.22em]">
            <div className="p-5">Area</div>
            <div className="p-5 border-l border-cream/20">The solo grind</div>
            <div className="p-5 border-l border-cream/20 bg-primary text-primary-foreground">Inside KCN</div>
          </div>
          {comparison.map(([area, a, b]) => (
            <div key={area} className="grid grid-cols-3 border-t border-cream-foreground/15">
              <div className="p-5 font-display text-lg text-cream-foreground">{area}</div>
              <div className="p-5 border-l border-cream-foreground/15 text-cream-foreground/80 flex items-start gap-3 text-base md:text-[1.05rem]">
                <span aria-hidden="true" className="mt-2.5 h-px w-3 bg-cream-foreground/50 shrink-0" />
                <span>{a}</span>
              </div>
              <div className="p-5 border-l border-cream-foreground/15 text-cream-foreground flex items-start gap-3 text-base md:text-[1.05rem]">
                <span aria-hidden="true" className="mt-2.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                <span>{b}</span>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section tone="cream">
        <div className="grid md:grid-cols-2 gap-12">
          {[
            {
              t: "You're a producer who's hit a ceiling",
              b: "You've done well. But every dollar of growth feels like another hour of your life. KCN gives you the leverage to break that pattern without rebuilding from scratch.",
            },
            {
              t: "You've thought about building a team - and stopped",
              b: "Hiring is hard. Leading is harder. We give you the org design, the scorecards, the playbook, and the mentorship to do it right the first time.",
            },
            {
              t: "You want faith and family to actually fit",
              b: "Most teams promise balance. We've built a culture that practices it. Calm, intentional, faith-aligned - and still wildly productive.",
            },
            {
              t: "You're ready to build wealth, not just income",
              b: "Income is what you earn. Wealth is what compounds. The COLE Method™ is designed to get you to the second one - and to keep you there.",
            },
          ].map((c) => (
            <div key={c.t} className="border-t border-cream-foreground/15 pt-8">
              <h3 className="font-display text-2xl text-cream-foreground mb-4">
                {c.t}
              </h3>
              <p className="text-cream-foreground/85 leading-relaxed">{c.b}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section tone="darker">
        <SectionEyebrow>Questions agents ask</SectionEyebrow>
        <h2 className="headline-lg mb-12 max-w-3xl">
          What you're probably wondering.
        </h2>
        <div className="divide-y divide-border border-y border-border">
          {[
            {
              q: "Who is the right fit for KCN?",
              a: "Growing and top-producing agents who want to build a real, scalable business - not just chase the next deal. Agents who value mentorship, systems, faith, and family alongside production.",
            },
            {
              q: "What does KCN provide that other teams don't?",
              a: "Four decades of proven leadership, refined systems, built-in leverage, collaborative support, and direct coaching - all designed to help you spend more time serving clients and less time managing the business.",
            },
            {
              q: "Do I have to live in Arizona or Alaska?",
              a: "Our primary markets include Arizona - Greater Phoenix, Northern Arizona, Southern Arizona, and the White Mountains - as well as Alaska, including Wasilla and Anchorage. We also have affiliated locations across the country. If you're interested in opportunities in your area, we'd love to connect and explore what's available.",
            },
            {
              q: "How do I start the conversation?",
              a: "Click below to schedule a one-on-one exploratory strategy call. We'll learn about your business, answer your questions, and determine if KCN is the right fit for your goals.",
              link: { to: "/strategy-call", label: "Book a growth strategy session" },
            },
          ].map((f) => (
            <details key={f.q} className="py-6 group">
              <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                <h3 className="font-display text-2xl pr-4">{f.q}</h3>
                <span className="text-primary text-2xl leading-none mt-1 group-open:rotate-45 transition-transform">
                  +
                </span>
              </summary>
              <p className="mt-4 text-foreground/90 leading-relaxed max-w-3xl">
                {f.a}
              </p>
              {"link" in f && f.link ? (
                <Link
                  to={f.link.to}
                  className="mt-5 inline-flex items-center gap-2 text-sm uppercase tracking-[0.22em] text-primary hover:text-foreground transition-colors"
                >
                  {f.link.label}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              ) : null}
            </details>
          ))}
        </div>
      </Section>

      {/* COACHED ON THE DATA */}
      <Section tone="dark">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionEyebrow>The unfair advantage</SectionEyebrow>
            <h2 className="headline-lg">
              Coached on the data.
              <br />
              <span className="serif-italic text-primary">Not the hype.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-foreground/90 text-lg leading-relaxed">
              Every Monthly Market Round Table begins with a review of current
              market data so our agents clearly understand the environment
              they're working in. We analyze local supply and demand, pricing
              trends, buyer and seller behavior, financing changes, and the
              frustrations consumers are experiencing in each market.
            </p>
            <p className="mt-4 text-foreground/85 leading-relaxed">
              From there, our leadership team introduces the Message of the
              Month - a clear strategy and conversation designed around the
              current market. Every agent is aligned around the same approach,
              giving you a relevant marketing message and prospecting strategy
              your database actually cares about. Instead of wondering what to
              say or how to position the market, you leave with practical
              conversations you can immediately use with buyers, sellers, and
              your sphere of influence.
            </p>
            <ul className="mt-8 divide-y divide-border border-y border-border">
              {[
                "The numbers that counter \"we'll just wait for rates to drop\"",
                "Cost-of-waiting math for sellers on the fence",
                "Local absorption and inventory data, by market",
                "Scripts tied to this month's data, not last year's",
              ].map((line) => (
                <li key={line} className="py-4 text-foreground/90 flex gap-4">
                  <span className="text-primary text-xs mt-2">◆</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* SIX PILLARS */}
      <Section tone="darker">

        <SectionEyebrow>What you actually get inside KCN</SectionEyebrow>
        <h2 className="headline-lg mb-4 max-w-3xl">
          Six pillars.{" "}
          <span className="serif-italic text-primary">One operating system.</span>
        </h2>
        <p className="text-foreground/80 max-w-2xl mb-12 text-lg leading-[1.7]">
          KCN isn't a brokerage logo. It's an integrated platform where every
          pillar reinforces the others - so growth compounds instead of stalls.
        </p>
        <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
          {PILLARS.map((p) => (
            <li key={p.slug} className="bg-background">
              <Link
                to={p.path}
                className="block p-8 h-full group hover:bg-surface transition-colors"
              >
                <p className="text-sm uppercase tracking-[0.22em] text-white mb-4">
                  {p.eyebrow}
                </p>
                <h3 className="font-display text-2xl md:text-3xl mb-3">
                  {p.title}{" "}
                  <span className="serif-italic text-foreground/80">
                    {p.italic}
                  </span>
                </h3>
                <p className="text-foreground/90 leading-relaxed mb-6">
                  {p.lead}
                </p>
                <span className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.22em] text-white">
                  Explore pillar{" "}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </Section>

      {/* VALUE CALLOUT */}
      <Section tone="cream" className="py-12 md:py-14">
        <div className="border border-cream-foreground/20 p-8 md:p-12 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <p className="text-[0.7rem] uppercase tracking-[0.22em] text-cream-foreground/60 mb-4">
              Go deeper
            </p>
            <h2 className="font-display text-3xl md:text-4xl text-cream-foreground leading-tight">
              Want the line-by-line{" "}
              <span className="serif-italic text-primary">value breakdown?</span>
            </h2>
            <p className="mt-4 text-cream-foreground/80 leading-relaxed max-w-2xl">
              Marketing, technology, leads, coaching, and admin support are all
              covered by the network. See exactly what you stop paying for out
              of pocket when you join.
            </p>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <Link
              to="/value"
              className="inline-flex items-center gap-2 border border-primary bg-primary text-primary-foreground px-7 py-4 text-sm uppercase tracking-[0.22em] hover:bg-transparent hover:text-primary transition-colors"
            >
              See the value
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </Section>

      <CTABanner cta="strategy" />
    </SiteLayout>
  );
}
